import { useUserAccount } from '@/api/user/queries';
import { GenericError } from '@/models/generic-error';
import { PaginatedResponse } from '@/models/pagination/paginated-response.dto';
import { DetailedUserReward } from '@/models/user-reward/detailed';
import { UserRewardsInputDto } from '@/services/dto/user-rewards/user-rewards-input.dto';
import { useInfiniteQueryCallbacks } from '@starsoft/common/hooks';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { UserRewardsQueryKeys } from '../../queryKeys';
import { UserRewardsMeQueryKey, UseUserRewardsMeProps } from './props';
import { fetchUserRewardsMe } from './service';

export function useUserRewardsMe(options?: UseUserRewardsMeProps) {
  const { rewardId, refetchOnMount = true } = options ?? {};
  const { isAuthorized } = useUserAccount();

  const filter: UserRewardsInputDto = {
    limit: 20,
    rewardId,
  };

  const queryKey: UserRewardsMeQueryKey = [UserRewardsQueryKeys.Me, filter];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  const { data, refetch, isPending, error, fetchStatus, ...rest } =
    useInfiniteQuery<
      PaginatedResponse<DetailedUserReward>,
      AxiosError<GenericError>,
      InfiniteData<PaginatedResponse<DetailedUserReward>>,
      UserRewardsMeQueryKey
    >({
      queryKey,
      queryFn: fetchUserRewardsMe,
      enabled: isAuthorized && !!rewardId,
      staleTime: 5 * 1000,
      refetchOnMount,
      getNextPageParam,
      getPreviousPageParam,
      initialPageParam: 1,
    });

  const rewards = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  const userRewards = useMemo(
    () =>
      data?.pages
        ?.flatMap(page => page?.data)
        ?.map(detailedReward => detailedReward?.reward),
    [data],
  );

  const reward = useMemo(() => rewards?.[0], [rewards]);

  return {
    error,
    refetch,
    isLoading: isPending && fetchStatus !== 'idle',
    data,
    userRewards,
    rewards,
    reward,
    ...rest,
  };
}
